import { Button } from '@profylr/ui'
import { PanelRightClose } from 'lucide-react'
import { createPortal } from 'react-dom'

import { useSidebarTheme, type SidebarTheme } from '@/contexts/sidebar-theme-context'

interface SettingsPanelProps {
  isOpen: boolean
  onClose: () => void
  headerHeight?: number
}

/**
 * The chrome preset chooser.
 *
 * Rendered through a portal to `document.body` rather than in place. It opens
 * from the user menu at the foot of the navigation rail, and the rail carries a
 * translate for its mobile off-canvas state -- a transformed ancestor makes
 * `position: fixed` resolve against that ancestor instead of the viewport, so
 * a drawer rendered inside it would dock to the sidebar rather than the screen.
 *
 * The portal also puts it outside the element carrying the chrome custom
 * properties, which is what it should be: the panel is a working surface, not
 * chrome, so it takes the canvas palette regardless of the preset it is
 * setting.
 */
const PRESETS: { value: SidebarTheme; label: string; description: string }[] = [
  { value: 'original', label: 'Brand', description: 'Purple chrome, following the theme' },
  { value: 'dark', label: 'Dark', description: 'Chrome darker than the canvas' },
  { value: 'white', label: 'Recede', description: 'Chrome takes the canvas colour' },
]

export function SettingsPanel({ isOpen, onClose, headerHeight = 72 }: SettingsPanelProps) {
  const { theme, setTheme } = useSidebarTheme()

  if (typeof document === 'undefined') return null

  return createPortal(
    <>
      {isOpen && <div className="fixed inset-0 z-40" onClick={onClose} />}

      <div
        role="dialog"
        aria-label="Navigation settings"
        aria-hidden={!isOpen || undefined}
        className={`fixed right-0 z-50 w-96 border-l border-divider bg-surface-raised shadow-dialog transition-transform duration-base ease-move ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ top: `${headerHeight}px`, height: `calc(100vh - ${headerHeight}px)` }}
      >
        <div className="p-8">
          <div className="mb-2 flex items-start justify-between">
            <div>
              <h2 className="m-0 p-0 text-heading-small text-text">Navigation Settings</h2>
              <p className="mt-1 text-body-small text-text-muted">Configure your preferences.</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              title="Close panel"
              aria-label="Close panel"
            >
              <PanelRightClose />
            </Button>
          </div>

          <div className="mt-8">
            <h3 className="m-0 p-0 text-body font-medium text-text">Sidebar style</h3>
            <p className="mb-4 text-body-small text-text-muted">
              Applies to the navigation rail and its panel. Light and dark for the rest of the
              interface is the toggle beside your avatar.
            </p>

            <div role="radiogroup" aria-label="Sidebar style" className="flex flex-col gap-2">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.value}
                  role="radio"
                  aria-checked={theme === preset.value}
                  variant={theme === preset.value ? 'primary' : 'secondary'}
                  onClick={() => setTheme(preset.value)}
                  className="h-auto flex-col items-start gap-0.5 px-4 py-3 text-left"
                >
                  <span className="text-body font-medium">{preset.label}</span>
                  <span className="text-body-small opacity-80">{preset.description}</span>
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  )
}
